import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Copy, Mail, MailOpen, MessageSquare } from "lucide-react";
import { api, copyText } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Button, Card, Input } from "@/components/app/Primitives";
import { PhoneInput } from "@/components/app/PhoneInput";
import { StackPage } from "@/components/app/Layout";

export function ForgotPassword() {
  const toast = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState("request");
  const [identifier, setIdentifier] = useState("");
  const [channel, setChannel] = useState("email");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const idn = () => (channel === "email" ? identifier.trim().toLowerCase() : identifier.trim());

  const request = async () => {
    if (!identifier.trim()) return toast.show("Enter your email or mobile", "error");
    setBusy(true);
    try {
      await api("/auth/forgot-password/request", { method: "POST", auth: false, body: { identifier: idn(), channel } });
      toast.show(channel === "email" ? "Code sent to your email" : "Code sent by SMS", "success");
      setStep("reset");
    } catch (e) { toast.show(e instanceof Error ? e.message : "Failed", "error"); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!code.trim()) return toast.show("Enter the 6-digit code", "error");
    if (pw.length < 6) return toast.show("Password must be at least 6 characters", "error");
    if (pw !== pw2) return toast.show("Passwords do not match", "error");
    setBusy(true);
    try {
      await api("/auth/forgot-password/reset", { method: "POST", auth: false, body: { identifier: idn(), code: code.trim(), new_password: pw } });
      toast.show("Password reset. Please sign in.", "success");
      navigate("/login", { replace: true });
    } catch (e) { toast.show(e instanceof Error ? e.message : "Failed", "error"); } finally { setBusy(false); }
  };

  return (
    <StackPage title="Forgot password" testID="forgot-password-page">
      <Card>
        {step === "request" ? (
          <div className="space-y-3">
            <p className="text-lg font-extrabold text-a-text">Send me a code</p>
            <p className="text-[13px] text-a-muted">We&apos;ll send a 6-digit code to your registered email or mobile.</p>
            <div className="flex gap-2">
              {["email", "sms"].map((c) => {
                const active = channel === c;
                const Icon = c === "email" ? Mail : MessageSquare;
                return (
                  <button key={c} data-testid={`fp-channel-${c}`} onClick={() => { setChannel(c); setIdentifier(""); }}
                    className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[13px] font-bold ${active ? "border-a-primary bg-a-primary text-white" : "border-a-border bg-a-surface text-a-text"}`}>
                    <Icon size={14} /> {c === "email" ? "Email" : "SMS"}
                  </button>
                );
              })}
            </div>
            {channel === "email" ? (
              <Input testID="fp-identifier" label="Email" type="email" placeholder="you@company.com" value={identifier} onChangeText={setIdentifier} />
            ) : (
              <PhoneInput testID="fp-identifier" label="Mobile number" placeholder="98765 43210" valueE164={identifier} onChangeE164={(e) => setIdentifier(e)} showValidationChecked={false} />
            )}
            <Button testID="fp-send" label="Send code" onPress={request} loading={busy} />
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-lg font-extrabold text-a-text">Enter the code</p>
            <p className="text-[13px] text-a-muted">Codes expire in 15 minutes.</p>
            <Input testID="fp-code" label="6-digit code" inputMode="numeric" placeholder="123456" maxLength={6} value={code} onChangeText={(t) => setCode(t.replace(/[^0-9]/g, ""))} />
            <Input testID="fp-new-password" label="New password" type="password" placeholder="6+ characters" value={pw} onChangeText={setPw} />
            <Input testID="fp-confirm-password" label="Confirm new password" type="password" placeholder="Re-enter password" value={pw2} onChangeText={setPw2}
              helper={pw2.length > 0 && pw2 !== pw ? "Passwords do not match" : undefined} />
            <Button testID="fp-reset" label="Reset password" onPress={reset} loading={busy} />
            <button onClick={() => setStep("request")} className="w-full text-center text-[13px] font-bold text-a-primary">Didn&apos;t get it? Send again</button>
          </div>
        )}
      </Card>
    </StackPage>
  );
}

export function ForgotEmail() {
  const toast = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState("request");
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [masked, setMasked] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const request = async () => {
    if (!mobile.trim()) return toast.show("Enter your registered mobile number", "error");
    setBusy(true);
    try {
      await api("/auth/forgot-email/request", { method: "POST", auth: false, body: { mobile_number: mobile, full_name: name.trim() || undefined } });
      toast.show("If that number exists, we sent a code by SMS.", "info");
      setStep("verify");
    } catch (e) { toast.show(e instanceof Error ? e.message : "Failed", "error"); } finally { setBusy(false); }
  };
  const verify = async () => {
    if (!code.trim()) return toast.show("Enter the code", "error");
    setBusy(true);
    try {
      const r = await api("/auth/forgot-email/verify", { method: "POST", auth: false, body: { mobile_number: mobile, code: code.trim() } });
      setEmail(r.email || ""); setMasked(r.masked_email); setStep("done");
    } catch (e) { toast.show(e instanceof Error ? e.message : "Failed", "error"); } finally { setBusy(false); }
  };
  const copyEmail = async () => {
    if (!email) return;
    await copyText(email);
    setCopied(true); toast.show("Email copied to clipboard", "success");
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <StackPage title="Forgot email ID" testID="forgot-email-page">
      <Card>
        {step === "request" ? (
          <div className="space-y-3">
            <p className="text-lg font-extrabold text-a-text">Verify by mobile</p>
            <p className="text-[13px] text-a-muted">We&apos;ll send a 6-digit code to your registered mobile. After verification we&apos;ll send the full email to your inbox.</p>
            <PhoneInput testID="fe-mobile" label="Mobile number *" placeholder="98765 43210" valueE164={mobile} onChangeE164={(e) => setMobile(e)} />
            <Input testID="fe-name" label="Full name (optional)" placeholder="Extra verification" value={name} onChangeText={setName} />
            <Button testID="fe-send" label="Send code" onPress={request} loading={busy} />
          </div>
        ) : step === "verify" ? (
          <div className="space-y-3">
            <p className="text-lg font-extrabold text-a-text">Enter the code</p>
            <p className="text-[13px] text-a-muted">Codes expire in 10 minutes.</p>
            <Input testID="fe-code" label="6-digit code" inputMode="numeric" placeholder="123456" value={code} onChangeText={setCode} />
            <Button testID="fe-verify" label="Verify" onPress={verify} loading={busy} />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-col items-center gap-2">
              <MailOpen size={36} className="text-a-success" />
              <p className="text-lg font-extrabold text-a-text">Verified</p>
            </div>
            <p className="text-center text-[13px] text-a-muted">Your registered email is</p>
            <div className="flex flex-col items-center gap-2">
              <p className="select-all text-center text-xl font-extrabold tracking-[0.5px] text-a-text" data-testid="fe-result-email">{email || masked}</p>
              {email ? (
                <button onClick={copyEmail} className={`flex items-center gap-1.5 rounded-full border border-a-border bg-a-surface px-3 py-1.5 text-xs font-bold ${copied ? "text-a-success" : "text-a-primary"}`}>
                  {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy email"}
                </button>
              ) : null}
            </div>
            <p className="text-center text-xs text-a-muted">Use this email to sign in. We&apos;ve also sent a security notification to that inbox.</p>
            <Button testID="fe-signin" label="Sign in now" onPress={() => navigate(email ? `/login?email=${encodeURIComponent(email)}` : "/login", { replace: true })} />
          </div>
        )}
      </Card>
    </StackPage>
  );
}
